import { useRef, useState } from "react";
import type { ApiError } from "@/lib/api";
import type {
  RosterImportCommitResult,
  RosterImportCommitRow,
  RosterImportDecision,
  RosterImportDirectoryMatch,
  RosterImportPreviewResult,
  RosterImportPreviewRow,
} from "@/lib/types";
import { CoachModal } from "./CoachModal";
import { Button } from "@/components/ui/Button";
import {
  DataTable,
  DataTableBody,
  DataTableCell,
  DataTableHead,
  DataTableHeaderRow,
  DataTableHeadCell,
  DataTableRow,
  DataTableScroll,
  DataTableShell,
} from "@/components/ui/DataTable";
import { TkticSelect } from "@/components/ui/TkticSelect";
import { useRosterImport } from "../hooks/useRosterImport";
import { useToast } from "@/components/ui/toast";
import { errorMessage } from "@/lib/api";
import { FileText, Upload } from "lucide-react";

/**
 * CSV roster import modal — Coach > Roster (M6b).
 *
 * Ports the legacy `openCoachRosterImportModal` +
 * `_renderRosterImportResult` (`js/coaching/roster.js`).
 *
 * Two-step flow:
 *   1. Choose a CSV file (or reveal the paste fallback) and preview it —
 *      the existing server-side dry-run renders the per-row plan.
 *      Writes nothing. #908 D16: a row whose name exactly matches an
 *      athlete already in the club directory is shown with that match
 *      for REVIEW — a per-row choice between the existing athlete (a new
 *      assignment of it) and a new athlete, defaulting to the API's
 *      `decision` (the match when exactly one). Nothing is linked
 *      without that explicit choice.
 *   2. "Commit import" — writes the import (transactional) with the
 *      reviewed decisions (`{ csv_text, decisions }`). Only
 *      enabled once a CLEAN (zero-error) preview exists AND the
 *      CSV hasn't changed since that preview (the staleness
 *      guard: editing the textarea after a preview forces a
 *      re-preview, so a coach can't commit a plan they never saw).
 *
 * Both endpoints are team-admin-only server-side. The launch
 * button (`RosterPage`) is also role-gated, so reaching this
 * modal as a non-admin shouldn't happen — a 403 is surfaced
 * inline as a backstop.
 *
 * Privacy: guardian-invite `token_url` values are surfaced ONLY
 * when the commit response carries them (dev-token mode); never
 * logged, never templated unconditionally.
 */

const SAMPLE_CSV = `display_name,jersey_number,position,guardian_email,relationship,active
Avery Lopez,4,Defender,parent@example.com,guardian,true
Mika Chen,9,Forward,parent@example.com,guardian,true`;

export type RosterImportModalProps = {
  onClose: () => void;
};

export function RosterImportModal({ onClose }: RosterImportModalProps) {
  const toast = useToast();
  const importer = useRosterImport();
  const [csv, setCsv] = useState("");
  const [selectedFileName, setSelectedFileName] = useState<string | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [showPaste, setShowPaste] = useState(false);
  // D16 review: row_number → chosen athlete id ("" = create a new athlete).
  // Seeded from the preview's `decision` defaults on every preview.
  const [decisions, setDecisions] = useState<Record<number, string>>({});
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const trimmedEmpty = csv.trim().length === 0;
  // Commit is allowed only with a clean, fresh preview of the
  // CURRENT csv text.
  const canCommit =
    !trimmedEmpty &&
    importer.previewHasNoErrors &&
    !importer.previewIsStale(csv) &&
    !importer.isCommitting;
  const showCommit = canCommit || importer.isCommitting;

  const handleClose = () => {
    importer.reset();
    setDecisions({});
    onClose();
  };

  const handlePreview = async () => {
    if (trimmedEmpty) return;
    try {
      const result = await importer.preview(csv);
      setDecisions(defaultDecisions(result));
    } catch {
      // Error surfaces via `importer.previewError` below.
    }
  };

  const handleCommit = async () => {
    if (trimmedEmpty) return;
    let rows = importer.previewResult?.rows ?? [];
    let reviewed = decisions;
    // Staleness guard: if the CSV changed since the last preview,
    // re-preview first so the coach commits exactly what they saw.
    if (importer.previewIsStale(csv)) {
      try {
        const fresh = await importer.preview(csv);
        reviewed = defaultDecisions(fresh);
        setDecisions(reviewed);
        rows = fresh.rows;
        if (fresh.summary.errors > 0) return; // errors block commit
      } catch {
        return;
      }
    }
    try {
      const result = await importer.commit({
        csv_text: csv,
        decisions: decisionList(rows, reviewed),
      });
      if (result.summary.errors === 0) {
        toast.success("Roster imported");
      } else {
        toast.error("Roster import finished with errors", {
          description: `${result.summary.errors} error row(s) — see the result list.`,
        });
      }
    } catch (err) {
      // Error also surfaces via `importer.commitError` below.
      toast.error("Couldn't import roster", { description: errorMessage(err) });
    }
  };

  const handleFile = async (file: File | undefined) => {
    if (!file) return;
    setFileError(null);
    if (!file.name.toLowerCase().endsWith(".csv")) {
      setFileError("Choose a .csv file.");
      return;
    }
    try {
      const contents = await file.text();
      if (!contents.trim()) {
        setFileError("This CSV file is empty.");
        return;
      }
      setCsv(contents);
      setSelectedFileName(file.name);
    } catch {
      setFileError("Could not read that CSV file.");
    }
  };

  const previewErrorText = errorText(importer.previewError);
  const commitErrorText = errorText(importer.commitError);

  return (
    <CoachModal
      title="Import roster"
      onClose={handleClose}
      data-testid="roster-import-modal"
      footer={
        importer.commitResult ? (
          <div className="flex justify-end">
            <Button
              type="button"
              onClick={handleClose}
              data-testid="roster-import-done"
            >
              Done
            </Button>
          </div>
        ) : (
          <div className="flex flex-wrap justify-end gap-2">
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handlePreview}
              disabled={trimmedEmpty || importer.isPreviewing}
              data-testid="roster-import-preview"
              variant="outline"
              className={showCommit ? "hidden" : undefined}
            >
              {importer.isPreviewing ? "Previewing…" : "Preview import"}
            </Button>
            <Button
              type="button"
              onClick={handleCommit}
              disabled={!canCommit}
              data-testid="roster-import-commit"
              className={showCommit ? undefined : "hidden"}
            >
              {importer.isCommitting ? "Importing…" : "Commit import"}
            </Button>
          </div>
        )
      }
    >
      <div className="flex flex-col gap-4">
        <section className="rounded border border-rule bg-elevated p-4">
          <div className="flex items-start gap-3">
            <span className="flex size-10 shrink-0 items-center justify-center rounded bg-accent/15 text-accent">
              <FileText aria-hidden="true" className="size-5" />
            </span>
            <div className="min-w-0 flex-1">
              <h3 className="font-display text-base text-ink">
                Choose a CSV file
              </h3>
              <p className="mt-1 text-sm leading-5 text-ink-muted">
                Nothing is added until you review and confirm the import.
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,text/csv"
                className="sr-only"
                tabIndex={-1}
                aria-hidden="true"
                data-testid="roster-import-file"
                onChange={(event) => {
                  void handleFile(event.target.files?.[0]);
                  event.currentTarget.value = "";
                }}
              />
              <div className="mt-3 flex flex-wrap items-center gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Upload aria-hidden="true" className="size-4" />
                  Choose file
                </Button>
                <span className="min-w-0 truncate text-sm text-ink-muted">
                  {selectedFileName ?? "No file selected"}
                </span>
              </div>
            </div>
          </div>
          {fileError ? (
            <p className="mt-3 text-xs text-destructive">{fileError}</p>
          ) : null}
        </section>

        {/* Column reference. */}
        <div className="rounded border border-rule bg-canvas p-3 text-xs text-ink-muted">
          <p className="font-display uppercase tracking-wider text-ink">
            CSV columns
          </p>
          <p className="mt-1">
            Required: <code>display_name</code>. Optional:{" "}
            <code>player_id</code>, <code>jersey_number</code>,{" "}
            <code>position</code>, <code>guardian_email</code>,{" "}
            <code>relationship</code>, <code>active</code>.
          </p>
        </div>

        <div>
          <button
            type="button"
            onClick={() => setShowPaste((current) => !current)}
            aria-expanded={showPaste}
            data-testid="roster-import-paste-toggle"
            className="rounded px-2 py-1 text-sm text-accent transition-colors hover:bg-field"
          >
            {showPaste ? "Hide pasted CSV" : "Paste CSV instead"}
          </button>
          {showPaste ? (
            <label className="mt-2 flex flex-col gap-2">
              <span className="text-xs font-display uppercase tracking-wider text-ink-muted">
                CSV content
              </span>
              <textarea
                value={csv}
                onChange={(event) => {
                  setCsv(event.target.value);
                  setSelectedFileName(null);
                }}
                spellCheck={false}
                rows={8}
                data-testid="roster-import-csv"
                placeholder="display_name,jersey_number,position,guardian_email,relationship,active"
                className="w-full rounded border border-rule bg-field px-3 py-2 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-accent"
              />
              <button
                type="button"
                onClick={() => {
                  setCsv(SAMPLE_CSV);
                  setSelectedFileName(null);
                }}
                data-testid="roster-import-sample"
                className="self-start rounded px-2 py-1 text-sm text-ink-muted transition-colors hover:bg-field hover:text-ink"
              >
                Use sample CSV
              </button>
            </label>
          ) : null}
        </div>

        {/* Staleness hint — the preview no longer matches the
         * textarea, so commit is gated until a re-preview. */}
        {importer.previewResult &&
        !importer.commitResult &&
        importer.previewIsStale(csv) &&
        !trimmedEmpty ? (
          <p
            className="text-ink-muted font-mono text-xs"
            data-testid="roster-import-stale-hint"
          >
            CSV changed since the last preview — preview again before
            importing.
          </p>
        ) : null}

        {/* Errors. */}
        {previewErrorText ? (
          <p
            className="text-destructive font-mono text-xs"
            data-testid="roster-import-preview-error"
          >
            {previewErrorText}
          </p>
        ) : null}
        {commitErrorText ? (
          <p
            className="text-destructive font-mono text-xs"
            data-testid="roster-import-commit-error"
          >
            {commitErrorText}
          </p>
        ) : null}

        {/* Result — commit result takes precedence over preview. */}
        {importer.commitResult ? (
          <CommitResultView result={importer.commitResult} />
        ) : importer.previewResult ? (
          <PreviewResultView
            result={importer.previewResult}
            decisions={decisions}
            onDecide={(rowNumber, athleteId) =>
              setDecisions((current) => ({ ...current, [rowNumber]: athleteId }))
            }
          />
        ) : null}
      </div>
    </CoachModal>
  );
}

/** The backend's actionable error message, or a generic fallback.
 * `apiFetch` already unwrapped FastAPI's `{ detail: ... }`
 * envelope, so `err.detail` IS the message string for the
 * 403 / 409 (wrong-team) / 422 import-error paths. */
function errorText(err: ApiError | null): string | null {
  if (!err) return null;
  if (typeof err.detail === "string") return err.detail;
  return "Something went wrong with the import.";
}

const ROW_LIMIT = 12;

/** D16: the preview's default per row — the single match when the API
 * says `use_existing`, otherwise a new athlete. Rows without a directory
 * match carry no entry (they always create). */
function defaultDecisions(
  result: RosterImportPreviewResult,
): Record<number, string> {
  const next: Record<number, string> = {};
  for (const row of result.rows) {
    const matches = row.directory_matches ?? [];
    if (matches.length === 0) continue;
    next[row.row_number] =
      row.decision === "use_existing" ? matches[0].athlete_id : "";
  }
  return next;
}

/** The reviewed choices the commit carries: one entry per row that was
 * offered a match; `athlete_id: null` = create a new athlete. */
function decisionList(
  rows: RosterImportPreviewRow[],
  decisions: Record<number, string>,
): RosterImportDecision[] {
  return rows
    .filter((row) => (row.directory_matches ?? []).length > 0)
    .map((row) => ({
      row_number: row.row_number,
      athlete_id: decisions[row.row_number] || null,
    }));
}

/** "<name> · born YYYY · Team · Season" — the match's disclosure (§5.3). */
function matchLabel(match: RosterImportDirectoryMatch): string {
  return [
    match.display_name,
    match.birth_year ? `born ${match.birth_year}` : null,
    ...match.assignments.map((a) =>
      [a.team_name, a.season_name].filter(Boolean).join(" · "),
    ),
  ]
    .filter(Boolean)
    .join(" · ");
}

function PreviewResultView({
  result,
  decisions,
  onDecide,
}: {
  result: RosterImportPreviewResult;
  decisions: Record<number, string>;
  onDecide: (rowNumber: number, athleteId: string) => void;
}) {
  const s = result.summary;
  const reviewCount = result.rows.filter(
    (row) => (row.directory_matches ?? []).length > 0,
  ).length;
  return (
    <div className="flex flex-col gap-2" data-testid="roster-import-preview-result">
      <SummaryRow
        items={[
          ["Rows", s.rows],
          ["Errors", s.errors],
          ["To create", s.create_players],
          ["To update", s.update_players],
          ["Guardian invites", s.guardian_invites],
          ["Existing guardians", s.linked_existing_users],
        ]}
      />
      {reviewCount > 0 ? (
        <p
          className="text-xs text-ink-muted"
          data-testid="roster-import-review-hint"
        >
          {reviewCount} row{reviewCount === 1 ? "" : "s"} match
          {reviewCount === 1 ? "es" : ""} an athlete already in the club
          directory. Choose the existing athlete to roster them again, or
          create a new athlete.
        </p>
      ) : null}
      <DataTableShell className="shadow-none">
        <DataTableScroll>
          <DataTable data-testid="roster-import-rows">
            <DataTableHead>
              <DataTableHeaderRow>
                <DataTableHeadCell>#</DataTableHeadCell>
                <DataTableHeadCell>Player</DataTableHeadCell>
                <DataTableHeadCell>Jersey</DataTableHeadCell>
                <DataTableHeadCell>Plan</DataTableHeadCell>
                <DataTableHeadCell>Athlete</DataTableHeadCell>
              </DataTableHeaderRow>
            </DataTableHead>
            <DataTableBody>
              {result.rows.map((row) => (
                <PreviewRowItem
                  key={row.row_number}
                  row={row}
                  decision={decisions[row.row_number] ?? ""}
                  onDecide={(athleteId) => onDecide(row.row_number, athleteId)}
                />
              ))}
            </DataTableBody>
          </DataTable>
        </DataTableScroll>
      </DataTableShell>
    </div>
  );
}

function CommitResultView({
  result,
}: {
  result: RosterImportCommitResult;
}) {
  const s = result.summary;
  const devInvites = result.guardian_invites.filter((inv) => inv.token_url);
  return (
    <div className="flex flex-col gap-2" data-testid="roster-import-commit-result">
      <p
        className="text-ink font-display text-sm"
        data-testid="roster-import-commit-success"
      >
        {s.errors === 0
          ? `Import complete — ${s.created_players} created, ${s.existing_players} existing.`
          : `Import finished with ${s.errors} error row(s).`}
      </p>
      <SummaryRow
        items={[
          ["Rows", s.rows],
          ["Errors", s.errors],
          ["Created", s.created_players],
          ["Existing", s.existing_players],
          ["Guardian invites", s.guardian_invites],
          ["Guardians linked", s.linked_existing_users],
        ]}
      />
      <RowList
        rows={result.rows}
        render={(row) => <CommitRowItem key={row.row_number} row={row} />}
      />

      {/* Dev-mode invite links — shown ONLY when the server sent
       * `token_url` (TKTIC_DEV_TOKEN_DELIVERY). Never in prod. */}
      {devInvites.length > 0 ? (
        <div
          className="rounded border border-rule bg-canvas p-2 text-xs"
          data-testid="roster-import-dev-invites"
        >
          <p className="font-display uppercase tracking-wider text-ink-muted">
            Dev invite links
          </p>
          {devInvites.map((inv) => (
            <p key={inv.id} className="font-mono mt-1 break-all">
              {inv.normalized_email}:{" "}
              <a
                href={inv.token_url}
                className="text-accent underline"
                target="_blank"
                rel="noreferrer"
              >
                {inv.token_url}
              </a>
            </p>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function SummaryRow({ items }: { items: Array<[string, number]> }) {
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
      {items.map(([label, value]) => (
        <span key={label} className="text-ink-muted">
          {label}:{" "}
          <strong className="text-ink tabular-nums">{value}</strong>
        </span>
      ))}
    </div>
  );
}

function RowList<T extends { row_number: number }>({
  rows,
  render,
}: {
  rows: T[];
  render: (row: T) => React.ReactNode;
}) {
  const shown = rows.slice(0, ROW_LIMIT);
  const overflow = rows.length - shown.length;
  return (
    <ul className="flex flex-col gap-1" data-testid="roster-import-rows">
      {shown.map(render)}
      {overflow > 0 ? (
        <li className="text-ink-muted text-xs font-mono px-2 py-1">
          +{overflow} more row{overflow === 1 ? "" : "s"} not shown
        </li>
      ) : null}
    </ul>
  );
}

function PreviewRowItem({
  row,
  decision,
  onDecide,
}: {
  row: RosterImportPreviewRow;
  decision: string;
  onDecide: (athleteId: string) => void;
}) {
  const isError = row.status === "error";
  const matches = row.directory_matches ?? [];
  return (
    <DataTableRow
      interactive={false}
      data-testid="roster-import-row"
      data-status={row.status}
      data-row-number={row.row_number}
      data-decision={
        matches.length > 0 ? (decision ? "use_existing" : "create") : undefined
      }
      className={isError ? "bg-destructive/5" : undefined}
    >
      <DataTableCell className="font-mono text-ink-muted">
        {row.row_number}
      </DataTableCell>
      <DataTableCell>
        <span className="text-ink font-display">
          {row.player?.display_name || "(missing name)"}
        </span>
        {row.player?.position ? (
          <span className="text-ink-muted"> · {row.player.position}</span>
        ) : null}
        {row.guardian_email ? (
          <span className="block text-ink-muted">{row.guardian_email}</span>
        ) : null}
        {row.warnings && row.warnings.length > 0 ? (
          <span className="block text-ink-muted">{row.warnings.join("; ")}</span>
        ) : null}
        {isError && row.errors && row.errors.length > 0 ? (
          <span className="block text-destructive">{row.errors.join("; ")}</span>
        ) : null}
      </DataTableCell>
      <DataTableCell className="font-mono text-ink-muted">
        {row.player?.jersey_number ? `#${row.player.jersey_number}` : "—"}
      </DataTableCell>
      <DataTableCell className="text-ink-muted">
        {!isError ? `${row.player_action} · ${row.guardian_action}` : "—"}
      </DataTableCell>
      <DataTableCell>
        {matches.length > 0 ? (
          <TkticSelect
            value={decision}
            onChange={onDecide}
            size="sm"
            ariaLabel={`Athlete for row ${row.row_number}`}
            data-testid={`roster-import-decision-${row.row_number}`}
            options={[
              { value: "", label: "Create new athlete" },
              ...matches.map((match) => ({
                value: match.athlete_id,
                label: matchLabel(match),
              })),
            ]}
          />
        ) : (
          <span className="text-ink-muted">
            {isError
              ? "—"
              : row.player_action === "update_player"
                ? "Existing roster row"
                : "New athlete"}
          </span>
        )}
      </DataTableCell>
    </DataTableRow>
  );
}

function CommitRowItem({ row }: { row: RosterImportCommitRow }) {
  const isError = row.status === "error";
  return (
    <li
      data-testid="roster-import-row"
      data-status={row.status}
      className={[
        "rounded border px-2 py-1 text-xs",
        isError
          ? "border-destructive/40 bg-destructive/5"
          : "border-rule bg-canvas",
      ].join(" ")}
    >
      <div className="flex flex-wrap items-baseline gap-x-2">
        <span className="font-mono text-ink-muted">#{row.row_number}</span>
        <span className="text-ink font-display">
          {row.player?.display_name || "(missing name)"}
        </span>
        {row.player?.jersey_number ? (
          <span className="text-ink-muted">#{row.player.jersey_number}</span>
        ) : null}
        {row.player?.position ? (
          <span className="text-ink-muted">· {row.player.position}</span>
        ) : null}
        {row.guardian_email ? (
          <span className="text-ink-muted">· {row.guardian_email}</span>
        ) : null}
      </div>
      {!isError ? (
        <div className="text-ink-muted mt-0.5" data-athlete-action={row.athlete_action}>
          {row.player_action} · {row.guardian_action}
          {row.athlete_action
            ? ` · ${row.athlete_action === "use_existing" ? "existing athlete" : "new athlete"}`
            : ""}
        </div>
      ) : null}
      {isError && row.errors && row.errors.length > 0 ? (
        <div className="text-destructive mt-0.5">{row.errors.join("; ")}</div>
      ) : null}
    </li>
  );
}
